import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://compliance-backend.cloudsanalytics.ai';

class ScaApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // Add request interceptor for Cognito token authentication
    this.api.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('cognito_token');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // If no token, reject the request
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch Security Configuration Assessment data for a specific agent
   * @param {string} agentId - The agent ID
   * @returns {Promise} API response
   */
  async getScaByAgent(agentId) {
    try {
      const response = await this.api.get(`/sca/${agentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SCA data:', error);
      throw error;
    }
  }

  /**
   * Alias for getScaByAgent to match the expected function name
   * @param {string} agentId - The agent ID
   * @returns {Promise} API response
   */
  async getScaData(agentId) {
    return this.getScaByAgent(agentId);
  }

  /**
   * Fetch latest SCA reports for a specific agent
   * @param {string} agentId - The agent ID
   * @returns {Promise} API response with latest reports
   */
  async getLatestReportsByAgent(agentId) {
    try {
      const response = await this.api.get(`/sca/${agentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching latest SCA reports:', error);
      throw error;
    }
  }

  /**
   * Fetch Security Configuration Assessment data for multiple agents
   * @param {Array} agentIds - Array of agent IDs
   * @returns {Promise} Combined API response
   */
  async getScaByMultipleAgents(agentIds) {
    try {
      const promises = agentIds.map(agentId => this.getScaByAgent(agentId));
      const responses = await Promise.all(promises);
      
      // Combine all responses into a single structure
      const combinedData = {
        data: {
          affected_items: [],
          total_affected_items: 0
        },
        agents: []
      };

      responses.forEach((response, index) => {
        if (response && response.data && response.data.affected_items) {
          // Add agent info to each item
          const agentItems = response.data.affected_items.map(item => ({
            ...item,
            agent_id: agentIds[index],
            agent_name: `Agent ${agentIds[index]}`
          }));
          
          combinedData.data.affected_items.push(...agentItems);
          combinedData.agents.push({
            id: agentIds[index],
            data: response.data
          });
        }
      });

      combinedData.data.total_affected_items = combinedData.data.affected_items.length;
      return combinedData;
    } catch (error) {
      console.error('Error fetching multiple SCA data:', error);
      throw error;
    }
  }
}

const scaApiService = new ScaApiService();

export default scaApiService;
